<script lang="ts" setup>
import {
  onMounted,
  ref,
  reactive,
  watch,
  onBeforeUnmount,
} from "vue";
import { PCheckbox } from "@profeat/ui-kit";
import { useI18n } from "vue-i18n";
import { onClickOutside, watchDebounced } from "@vueuse/core";

import { customerConstants } from "~/components/customers/helper";
import { TypeComparators } from "~/entities/customer";
import { SystemField } from "~/entities/contacts";
import { FilterBuilder } from "~/components/customers/filter-builder";

const { DEFAULT_FILTER_DEBOUNCE } = customerConstants;
const { t } = useI18n();

const props = defineProps<{ field: SystemField; filterBuilder: FilterBuilder; refWrap: HTMLElement }>();
const emit = defineEmits<{
  (event: "applyFilter", visible?: boolean, withoutCall?: boolean): void;
  (event: "resetFilter", withoutCall?: boolean): void;
  (event: "clickOutside"): void;
  (event: "update:changed", val: boolean): void;
}>();

const dataForStatus = [
  {
    value: "active",
    label: "active",
    ref: false,
  },
  {
    value: "new",
    label: "new",
    ref: false,
  },
  {
    value: "lost",
    label: "lost",
    ref: false,
  },
  {
    value: "archive",
    label: "archive",
    ref: false,
  },
];
const dataForUploadMethod = [
  {
    value: "manual",
    label: "manual",
    ref: false,
  },
  {
    value: "incoming",
    label: "incoming",
    ref: false,
  },
  {
    value: "api",
    label: "api",
    ref: false,
  },
];

const data =
  props.field.name === "status" ? dataForStatus : dataForUploadMethod;
const types = reactive(data);
const translator =
  props.field.name === "status"
    ? "customers.statuses."
    : "customers.uploadMethods.";

const filterRef = ref(null);
const isMountUpdate = ref(false);
const isUnmounted = ref(false);

onBeforeUnmount(() => {
  isUnmounted.value = true;
});

let typesInit = JSON.parse(JSON.stringify(data));

const getFieldName = (label: string): string => {
  return t(`${translator}${label}`);
};
const setToFilter = (visible = false, withoutCall = false) => {
  if (JSON.stringify(typesInit) === JSON.stringify(types)) return;

  if (types.every((el) => !el.ref)) {
    const hasFilter = !!props.filterBuilder.getFilterById(props.field.id);

    if (hasFilter) emit("resetFilter");

    return;
  }

  props.filterBuilder.type(
    props.field.name as TypeComparators,
    props.field.id,
    types.filter((el) => el.ref).map((el) => el.label)
  );

  emit("applyFilter", visible, withoutCall);
};

onClickOutside(
  () => props.refWrap,
  () => {
    setToFilter();
    emit("clickOutside");
  }
  );
watchDebounced(
  types,
  () => {
    if (isUnmounted.value) return;
    if (!isMountUpdate.value) {
      setToFilter(true);
    }
  },
  { debounce: DEFAULT_FILTER_DEBOUNCE * 2, deep: true }
);
onMounted(() => {
  const filters = props.filterBuilder.getFilterById(props.field.id);

  if (filters) {
    isMountUpdate.value = true;
    types.forEach((type) =>
      filters.includes(type.label) ? (type.ref = true) : false
    );
    typesInit = JSON.parse(JSON.stringify(types));
  }
});

watch(
  types,
  (val) => {
    const changed = JSON.stringify(typesInit) !== JSON.stringify(val);
    emit("update:changed", changed);
  },
  { deep: true },
);
</script>

<template>
  <div class="type-filter" ref="filterRef">
    <div class="values">
      <PCheckbox v-for="type in types" v-model="type.ref" @update:model-value="() => (isMountUpdate = false)"
        :key="type.value" :label="getFieldName(type.label)" label-position="right" text-size="sm" />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.type-filter {
  .values {
    display: flex;
    flex-direction: column;
    grid-gap: 10px;
  }
}
</style>
